import React, { useEffect, useId, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGoogleMaps } from '../partials/GoogleMapsLoader';
import PlaceAutocompleteInput from '../partials/PlaceAutocompleteInput';
import { ADDRESS_SEARCH_VERSION } from '../utils/recentSearch';

const ITALY_REGION_CODES = ['it'];

function SearchFields({ mapsStatus }) {
    const id = useId();
    const navigate = useNavigate();
    const placeInputRef = useRef(null);
    const requestRef = useRef(null);
    const mountedRef = useRef(true);
    const [location, setLocation] = useState({ address: '', lat: null, lng: null });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
            requestRef.current?.abort();
        };
    }, []);

    const mapsError = mapsStatus === 'missing' || mapsStatus === 'error'
        ? "La ricerca per località non è al momento disponibile. Contattaci per trovare un'agenzia nella tua zona."
        : '';

    function handlePlaceSelected(place) {
        setLocation({ address: place.address, lat: place.lat, lng: place.lng });
        setError('');
    }

    function handleAddressChange(address) {
        setLocation({ address, lat: null, lng: null });
        setError('');
    }

    async function handleSubmit(event) {
        event.preventDefault();
        if (requestRef.current) return;
        if (mapsStatus !== 'ready') {
            setError(mapsError || 'Attendi il caricamento dei suggerimenti per la località.');
            return;
        }
        if (!Number.isFinite(location.lat) || !Number.isFinite(location.lng)
            || placeInputRef.current?.value !== location.address) {
            const message = 'Seleziona una città o un indirizzo dall’elenco dei suggerimenti.';
            setError(message);
            return;
        }
        if (!event.currentTarget.reportValidity()) return;

        const apiUrl = process.env.REACT_APP_AUTH_API_URL?.trim();
        if (!apiUrl) {
            setError("La ricerca non è al momento disponibile. Contattaci: ti aiuteremo a trovare l'agenzia più adatta.");
            return;
        }

        const controller = new AbortController();
        requestRef.current = controller;
        let timedOut = false;
        const timeout = window.setTimeout(() => {
            timedOut = true;
            controller.abort();
        }, 20000);
        setIsSubmitting(true);
        setError('');

        try {
            const response = await fetch(`${apiUrl.replace(/\/$/, '')}/auth/getFuneralHonorsByFilters`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                signal: controller.signal,
                body: JSON.stringify({
                    funeralHonorLat: location.lat,
                    funeralHonorLng: location.lng,
                }),
            });
            if (!response.ok) throw new Error('Search request failed');
            const cards = await response.json();
            if (!Array.isArray(cards)) throw new Error('Invalid search response');
            if (mountedRef.current) {
                navigate('/risultati', { state: { cards, searchLocation: location.address, searchVersion: ADDRESS_SEARCH_VERSION } });
            }
        } catch (requestError) {
            if (mountedRef.current) {
                setError(timedOut
                    ? 'La ricerca sta richiedendo troppo tempo. Riprova tra qualche istante o contattaci.'
                    : 'Non è stato possibile completare la ricerca. Riprova o contattaci per ricevere assistenza.');
            }
        } finally {
            window.clearTimeout(timeout);
            requestRef.current = null;
            if (mountedRef.current) setIsSubmitting(false);
        }
    }

    const addressInput = (
        <input
            id={`${id}-location`}
            name="location"
            type="text"
            placeholder="Indirizzo o città (es. via Roma, Milano)"
            autoComplete="off"
            value={location.address}
            onChange={event => handleAddressChange(event.target.value)}
            aria-describedby={error || mapsError ? `${id}-status` : undefined}
            disabled={isSubmitting}
            required
        />
    );

    return (
        <form id="trova-onoranze-funebri" className="ef-search-form" onSubmit={handleSubmit} aria-labelledby={`${id}-title`} aria-busy={isSubmitting}>
            <h3 id={`${id}-title`} className="ef-eyebrow">Trova l'agenzia giusta per te</h3>
            <div className="ef-search-fields">
                <div className="ef-field">
                    <label htmlFor={`${id}-location`}>Dove hai bisogno del servizio?</label>
                    {mapsStatus === 'ready' ? (
                        <PlaceAutocompleteInput
                            id={`${id}-location`}
                            name="location"
                            placeholder="Indirizzo o città (es. via Roma, Milano)"
                            description="Dove hai bisogno del servizio?"
                            regionCodes={ITALY_REGION_CODES}
                            value={location.address}
                            disabled={isSubmitting}
                            fallback={addressInput}
                            onReady={element => { placeInputRef.current = element; }}
                            onInput={handleAddressChange}
                            onPlaceSelect={handlePlaceSelected}
                            onError={() => setError('I suggerimenti per la località non sono disponibili. Riprova più tardi.')}
                        />
                    ) : addressInput}
                </div>
            </div>
            <button className="ef-button ef-button--primary ef-search-submit" type="submit" disabled={isSubmitting || mapsStatus === 'loading'}>
                {isSubmitting ? 'Ricerca in corso...' : <>Trova le agenzie vicine <span aria-hidden="true">→</span></>}
            </button>
            <div id={`${id}-status`} className={`ef-form-status${error || mapsError ? ' ef-form-status--error' : ''}`} role="status" aria-live="polite" aria-atomic="true">
                {error || mapsError || (mapsStatus === 'loading' ? 'Caricamento dei suggerimenti per la località...' : '')}
            </div>
        </form>
    );
}

function MapsSearchForm({ apiKey }) {
    const { isLoaded, loadError } = useGoogleMaps(apiKey);

    return <SearchFields mapsStatus={loadError ? 'error' : isLoaded ? 'ready' : 'loading'} />;
}

export default function SearchForm() {
    const apiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY?.trim();
    return apiKey ? <MapsSearchForm apiKey={apiKey} /> : <SearchFields mapsStatus="missing" />;
}
